// src/components/DarkWebNewDealModal.js
import React, { useState, useEffect } from "react";
import { X, Plus, Loader } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useModuleAvailability } from "../hooks/useModuleAvailability";
import CreateDealModal from "./CreateDealModal";

const DarkWebNewDealModal = ({ isOpen, onClose, refreshDeals, onDealSelected, existingDealIds = [] }) => {
    const [deals, setDeals] = useState([]);
    const [selectedDealId, setSelectedDealId] = useState("");
    const [domain, setDomain] = useState("");
    const [enableLiveMonitoring, setEnableLiveMonitoring] = useState(false);
    const [showNewDealModal, setShowNewDealModal] = useState(false);
    const [loadingDeals, setLoadingDeals] = useState(true);
    const [saving, setSaving] = useState(false);

    const apiCall = useApi();
    // Live monitoring (Enzoic) needs internet access; on Restricted/AirGapped installations the
    // checkbox is hidden and no enrollment call is made. The assessment itself is local.
    const { loaded: flagsLoaded, isModuleAvailable } = useModuleAvailability();
    const liveMonitoringAvailable = flagsLoaded && isModuleAvailable("darkWebMonitoring");
    const org = JSON.parse(localStorage.getItem("selectedOrganization"));
    const organizationId = org?.organizationId;

    // Fetch all deals when modal opens
    useEffect(() => {
        if (isOpen) fetchDeals();
    }, [isOpen]);

    // Auto-open create modal if no deals available
    useEffect(() => {
        if (isOpen && !loadingDeals && deals.length === 0) {
            setShowNewDealModal(true);
        }
    }, [isOpen, loadingDeals, deals.length]);

    const fetchDeals = async () => {
        try {
            const res = await apiCall("GET", `/deals`);
            // Filter out deals that are already added for this product category
            const availableDeals = (res || []).filter(d => !existingDealIds.includes(d.dealID));
            setDeals(availableDeals);
        } catch (err) {} finally {
            setLoadingDeals(false);
        }
    };

    // Handle new deal creation from nested modal
    const handleNewDealCreated = async (newDeal) => {
        setShowNewDealModal(false);
        // Close both modals and trigger refresh via parent callback
        onDealSelected?.(newDeal);
        onClose();
    };

    // Handle save (deal selection confirmation)
    // Handle save (deal selection confirmation)
    const handleSave = async (e) => {
        e.preventDefault();

        if (!selectedDealId) {
            alert("Please select a deal first.");
            return;
        }

        const selectedDeal = deals.find((d) => d.dealID.toString() === selectedDealId);

        setSaving(true);
        try {
            // ✅ Step 1: Update DealCategory and domain in backend
            await apiCall("PUT", `/deals/dealUpdate-category/${selectedDealId}`, {dealCategory: "DarkWeb", domain: domain});

            // ✅ Step 2: If live monitoring enabled and domain provided, initialize Enzoic
            if (liveMonitoringAvailable && enableLiveMonitoring && domain.trim()) {
                try {
                    await apiCall("POST", `/enzoic/initialize-deal/${selectedDealId}`, { domain: domain.trim() });
                } catch (monitoringError) {}
            }

            // ✅ Step 3: Trigger parent callback and close modal
            onDealSelected?.(selectedDeal);
            onClose();
        } catch (error) {
            alert("Failed to update deal category. Check console for details.");
        } finally {
            setSaving(false);
        }
    };


    if (!isOpen) return null;

    return (
        <>
            {/* Assign Deal Modal */}
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-6 border border-border w-full max-w-lg shadow-lg rounded-md bg-theme-secondary">

                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-content-primary">Select or Create Deal</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSave} className="space-y-4">
                        {/* Deal Dropdown + Add Button */}


                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Deal</label>
                            <div className="flex space-x-2 w-full">
                                <select
                                    value={selectedDealId}
                                    onChange={(e) => {
                                        const id = e.target.value;
                                        setSelectedDealId(id);
                                        const deal = deals.find((d) => d.dealID.toString() === id);
                                        setDomain(deal?.domain || "");
                                    }}
                                    className="flex-grow min-w-0 border border-gray-300 rounded-md px-3 py-2 truncate"
                                    disabled={loadingDeals}
                                    style={{ maxWidth: "calc(100% - 40px)" }} // leave room for + button
                                >
                                    <option value="">-- Select a Deal --</option>
                                    {deals.map((d) => (
                                        <option
                                            key={d.dealID}
                                            value={d.dealID}
                                            className="truncate"
                                            title={d.dealName}
                                        >
                                            {d.dealName}
                                        </option>
                                    ))}
                                </select>

                                <button
                                    type="button"
                                    onClick={() => setShowNewDealModal(true)}
                                    className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex-shrink-0 flex items-center justify-center"
                                    title="Add New Deal"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Domain field - shown when a deal is selected */}
                        {selectedDealId && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                                <input
                                    type="text"
                                    value={domain}
                                    onChange={(e) => setDomain(e.target.value)}
                                    placeholder="e.g., example.com"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                                />
                                <p className="mt-1 text-sm text-gray-500">
                                    Please verify or update the domain name related to the deal, or leave blank if none. We will scan the Dark Web for any exposures related to the deal.
                                </p>
                            </div>
                        )}

                        {/* Enable Live Monitoring checkbox - shown when live monitoring is available and a deal is selected */}
                        {selectedDealId && liveMonitoringAvailable && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="enableLiveMonitoring"
                                    checked={enableLiveMonitoring}
                                    onChange={(e) => setEnableLiveMonitoring(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="enableLiveMonitoring" className="ml-2 text-sm text-gray-700">
                                    Enable Live Monitoring
                                </label>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex justify-end space-x-3 mt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving ? (
                                    <>
                                        <Loader className="h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    "Save"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Nested New Deal Modal */}
            <CreateDealModal
                isOpen={showNewDealModal}
                onClose={deals.length === 0 ? onClose : () => setShowNewDealModal(false)}
                onDealCreated={handleNewDealCreated}
                dealCategory="DarkWeb"
                organizationId={organizationId}
                title="Create New Deal"
            />
        </>
    );
};

export default DarkWebNewDealModal;
